import React, { FunctionComponent, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { AxiosError, AxiosResponse } from 'axios';
import {
  Button, Callout, Icon, InputGroup, Tab, Tabs
} from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { RootState } from 'data/root.reducers';
import { ServerResponse } from 'data/types';
import {
  EWarrantDecisionStatus,
  EWarrantDocument,
  EWarrantGeneration,
  EWarrantGenerateTemplate,
  EWarrantInterceptStatusValue,
  EWarrantPrivileges,
  EWarrantServices,
  EWarrantStatus,
  EWarrantTaskStatus
} from 'data/eWarrant/eWarrant.types';
import * as actions from 'data/eWarrant/eWarrant.actions';
import { AWAITING_UPDATE_APPROVAL_STATUS } from 'data/eWarrant/eWarrant.adapter';
import * as selectors from 'data/eWarrant/eWarrant.selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as userSelectors from 'users/users-selectors';
import * as contactSelectors from 'data/contact/contact.selectors';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import * as cfFtpInfoSelectors from 'data/cfFtpInfo/cfFtpInfo.selectors';
import * as networkSelectors from 'data/network/network.selectors';
import * as securityInfoSelectors from 'data/securityInfo/securityInfo.selectors';
import * as fqdnGroupSelectors from 'data/fqdnGroup/fqdnGroup.selectors';
import * as nodeSelectors from 'data/nodes/nodes.selectors';
import * as locationSelectors from 'data/location/location.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as accessFunctionActions from 'data/accessFunction/accessFunction.actions';
import * as collectionFunctionActions from 'data/collectionFunction/collectionFunction.actions';
import WarrantConfiguration from 'xcipio/config/warrant-config';
import buildCollectionFunctionsFromEWarrant from 'routes/EWarrant/utils/buildCollectionFunctionsFromEWarrant';
import findLeaForEWarrant from 'routes/EWarrant/utils/findLeaForEWarrant';
import { getCaseOptionTemplateValue } from 'xcipio/warrants/forms/warrant-panel';
import { buildDecisionMessage, parseDecisionMessage } from 'routes/EWarrant/utils/decisionMessage';
import {
  areAllDocumentsApproved,
  buildDocumentStatusPayload,
  DocumentDecisions,
  seedDocumentDecisions,
  toggleDocumentDecision
} from 'routes/EWarrant/utils/documentDecisions';
import {
  areAllTargetsApproved,
  getBlockingTargets,
  getTargetDecision,
  isTargetDecidable,
  recordTargetDecision,
  seedTargetDecisions,
  TargetDecisions
} from 'routes/EWarrant/utils/targetDecisions';
import downloadDocument from 'routes/EWarrant/utils/downloadDocument';
import { FormLayout, FormLayoutContent } from 'components/FormLayout';
import FormPanel from 'components/FormPanel';
import FormButtons from 'components/FormButtons';
import { RetrievingDataProgress } from 'components';
import { dateTimeFormatter } from 'shared/constants';
import {
  showError, showSuccessMessage, TOAST_TIMEOUT
} from 'shared/toaster';
import { extractErrorMessage } from 'utils';
import EWarrantKeyValueGrid, { NOT_AVAILABLE } from 'routes/EWarrant/components/EWarrantKeyValueGrid';
import EWarrantDocumentTable from 'routes/EWarrant/components/EWarrantDocumentTable';
import EWarrantStatusCellRenderer from 'routes/EWarrant/components/EWarrantStatusCellRenderer';
import './e-warrant-view.scss';

type RouterParams = {
  history: string
};

/**
 * The shape WarrantConfiguration expects for its two lookup dependencies. connect()
 * types the bound action creators as returning the action object, but
 * redux-promise-middleware resolves them to a promise at dispatch time — the same
 * assertion warrant-frame.tsx makes in its own props type.
 */
type LookupByFilter = (deliveryFunctionId: string, filter: string) => Promise<any>;

enum EWarrantViewTab {
  Authorisation = 'authorisation',
  Documents = 'documents',
  Services = 'services'
}

const SERVICE_TAB_ID_PREFIX = 'service-';

/**
 * The task status values that close Generate Warrant. A target the LEA has suspended or
 * withdrawn is not one Xcipio should be provisioning an intercept for, so a single one of
 * them is enough to stop the whole e-warrant being generated.
 *
 * Compared case insensitively: the status is Xcipio's own string, carried through
 * untranslated, so its casing is not the portal's to rely on.
 */
const TARGET_STATUSES_BLOCKING_GENERATE = ['suspended', 'cancelled'];

const blocksGenerate = (status: string) =>
  TARGET_STATUSES_BLOCKING_GENERATE.includes((status ?? '').trim().toLowerCase());

/**
 * The case options templates the backend picks between, by name: a generated case uses
 * the first when its service asks for location and the second when it does not. Matched
 * on the name rather than the id because template ids differ per deployment, and the
 * backend resolves them the same way.
 */
const CASE_OPTIONS_TEMPLATE_NAMES = ['With Location', 'Without Location'];

const mapState = (state: RootState) => ({
  eWarrant: selectors.getSelectedEWarrant(state),
  privileges: authenticationSelectors.getAuthenticationPrivileges(state),
  // Stamped onto the decision message, so the e-warrant records who decided it.
  userName: authenticationSelectors.getAuthenticationUserName(state),
  // Raw store slices — the collection function templates are only resolved from them
  // when a warrant is actually generated, so nothing is recomputed on every render.
  collectionFunctionTemplates: templateSelectors.getEnabledCollectionFunctionFormTemplatesByKey(state),
  enabledCollectionFunctions: supportedFeaturesSelectors.getEnabledCollectionFunctions(state),
  // The generated cases point at one of these by id, so the pair travels with the
  // generate request — see CASE_OPTIONS_TEMPLATE_NAMES below.
  caseOptionsTemplates: templateSelectors.getCaseOptionsTemplates(state),
  // Stamped onto the generated warrant as its templateUsed, the way a form-created
  // warrant records the template it was built from.
  warrantTemplates: templateSelectors.getRawWarrantTemplates(state),
  // Needed to build a WarrantConfiguration at generate time — it derives which AF
  // types, CF types and services this delivery function actually has enabled.
  supportedFeatures: supportedFeaturesSelectors.getSupportedFeatures(state),
  selectedDF: globalSelectors.getSelectedDeliveryFunctionId(state),
  deployment: discoverySelectors.getDiscoveryDeployment(state),
  timezones: userSelectors.getTimezones(state),
  contacts: contactSelectors.getContacts(state),
  collectionFunctionGroupList: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
  cfFtpInfos: cfFtpInfoSelectors.getCfFtpInfoList(state),
  networks: networkSelectors.getNetworks(state),
  securityInfos: securityInfoSelectors.getSecurityInfos(state),
  fqdnGroups: fqdnGroupSelectors.getFqdnGroups(state),
  xcipioNodes: nodeSelectors.getNodes(state),
  countries: locationSelectors.getCountryList(state),
  is5GCVcpEnabled: supportedFeaturesSelectors.is5gcVcpEnabled(state),
  isLocationRequired: discoverySelectors.isLocationRequired(state),
  isT1678HITlsEnabled: discoverySelectors.isT1678HITlsEnabled(state),
  is33108HITlsEnabled: discoverySelectors.is33108HITlsEnabled(state),
});

const mapDispatch = {
  goToEWarrantList: actions.goToEWarrantList,
  getEWarrantDetail: actions.getEWarrantDetail,
  updateAuthStatus: actions.updateAuthStatus,
  updateTaskStatus: actions.updateTaskStatus,
  generateWarrant: actions.generateWarrant,
  // WarrantConfiguration takes these in its props. Only its async methods use them,
  // and the generate flow calls none of those — but they are wired properly rather
  // than stubbed so the instance behaves like the one the warrant wizard builds.
  getAllAFsByFilter: accessFunctionActions.getAllAFsByFilter,
  fetchCollectionFunctionListByFilter: collectionFunctionActions.fetchCollectionFunctionListByFilter,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;

export type Props = PropsFromRedux & PropsFromRouter;

const formatDateTime = (value?: string | null): string =>
  (value ? dateTimeFormatter(value) : NOT_AVAILABLE);

/**
 * The decision block's classes: the panel itself, plus the modifier that tints it by the
 * state it is showing — awaiting a decision, or reporting the one that was made.
 */
const decisionBarClass = (state: 'pending' | 'approved' | 'rejected'): string =>
  `e-warrant-view-decision-bar e-warrant-view-decision-bar-${state}`;

/**
 * One thing standing in the way of approving the e-warrant. `isDetail` marks an entry that
 * elaborates the one above it — a single blocking target under the line naming them — so it
 * can be set in from the margin rather than reading as a separate demand.
 */
type ApprovalBlocker = {
  text: string
  isDetail?: boolean
};

/**
 * The reasons a decision is closed, one to a line.
 *
 * A list rather than one joined sentence: an e-warrant tasking several targets has a reason
 * per target, and running them together left the tooltip an unreadable wall of text.
 */
const renderApprovalBlockers = (blockers: ApprovalBlocker[]) => (
  <ul className="e-warrant-view-blockers">
    {blockers.map(({ text, isDetail }) => (
      <li key={text} className={isDetail ? 'e-warrant-view-blocker-detail' : undefined}>
        {text}
      </li>
    ))}
  </ul>
);

/** The target decision currently being written, identified by the LIID it is being written to. */
type TargetDecisionInFlight = {
  reference: string
  status: EWarrantTaskStatus
};

/**
 * The modifier tinting a target's action bar by the decision it is showing, in step with
 * decisionBarClass above — approved, rejected, or still awaiting a decision.
 */
const targetActionsClass = (decision: EWarrantTaskStatus | null): string => {
  if (decision === EWarrantTaskStatus.Active) {
    return 'e-warrant-view-target-actions e-warrant-view-target-actions-approved';
  }

  if (decision === EWarrantTaskStatus.Rejected) {
    return 'e-warrant-view-target-actions e-warrant-view-target-actions-rejected';
  }

  return 'e-warrant-view-target-actions e-warrant-view-target-actions-pending';
};

export const EWarrantView: FunctionComponent<Props> = ({
  eWarrant,
  privileges,
  userName,
  history,
  goToEWarrantList,
  getEWarrantDetail,
  updateAuthStatus,
  updateTaskStatus,
  generateWarrant,
  supportedFeatures,
  selectedDF,
  deployment,
  caseOptionsTemplates,
  warrantTemplates,
  getAllAFsByFilter,
  fetchCollectionFunctionListByFilter,
  ...collectionFunctionTemplateContext
}: Props) => {
  const [selectedTab, setSelectedTab] = useState<EWarrantViewTab>(EWarrantViewTab.Authorisation);
  const [selectedServiceTab, setSelectedServiceTab] = useState(`${SERVICE_TAB_ID_PREFIX}0`);
  const [decisionNote, setDecisionNote] = useState('');
  // Which decision is in flight, so only the button that was clicked spins and the other
  // one cannot be clicked behind it. null when no decision is being recorded.
  const [decisionInFlight, setDecisionInFlight] = useState<EWarrantDecisionStatus | null>(null);
  // Which target decision is being written — the LIID of the target and the status it is
  // being moved to — so only the button that was clicked spins and no second target can be
  // decided behind it. null when no target is being decided.
  const [targetInFlight, setTargetInFlight] = useState<TargetDecisionInFlight | null>(null);
  // What the operator has ticked per document on the Documents tab, until a decision is
  // submitted. Xcipio owns the persisted DOCSTATUS, so a tick is never written through on
  // its own — the whole set travels with the Approve or Reject that follows.
  const [documentDecisions, setDocumentDecisions] = useState<DocumentDecisions>({});
  // What Xcipio holds against each target, keyed by LIID. Unlike the document ticks above
  // this is not unsaved input: a target's decision is written the moment its button is
  // clicked, and this records what came back, so one target's write does not have to drag a
  // re-read of the whole e-warrant behind it.
  const [targetDecisions, setTargetDecisions] = useState<TargetDecisions>({});
  // The generate flow spans two requests, so the busy flag is owned here rather than
  // taken from the per-action flag in the store.
  const [isGenerating, setIsGenerating] = useState(false);
  // Drives the blocking loading popup shown while refreshDetail is re-reading the
  // e-warrant — after a decision, and after a successful generate.
  const [isRefreshingDetail, setIsRefreshingDetail] = useState(false);
  const { t } = useTranslation();

  const selectedEWarrantId = eWarrant?.id;
  const selectedSenderId = eWarrant?.authorisation?.SENDERID;

  // The list read carries no documents and no tasks, so everything below the
  // Authorisation tab — and the generate flow, which needs each task's handover format —
  // depends on this detail read.
  //
  // Both halves of the key are required: an e-warrant that reached the store without a
  // list read would have no SENDERID, and a half-keyed read could pick up another
  // sender's row.
  useEffect(() => {
    if (selectedEWarrantId != null && selectedSenderId != null && selectedSenderId !== '') {
      getEWarrantDetail(selectedEWarrantId, selectedSenderId);
    }
  }, [selectedEWarrantId, selectedSenderId, getEWarrantDetail]);

  /**
   * Starts the checkboxes off on the decisions Xcipio already holds.
   *
   * The adapter builds a fresh documents array out of every detail read, so this runs on
   * mount and again after each refresh below — which is what makes the columns show the
   * decision that was just recorded once the e-warrant is no longer pending. Nothing else
   * replaces that array, so an operator's unsaved ticks are not reset underneath them.
   */
  useEffect(() => {
    setDocumentDecisions(seedDocumentDecisions(eWarrant?.documents ?? []));
  }, [eWarrant?.documents]);

  /**
   * Starts the target tabs off on the decisions Xcipio already holds.
   *
   * The adapter builds a fresh services array out of every detail read, so this runs on
   * mount and again whenever the e-warrant is re-read — after the e-warrant's own decision,
   * and after a generate. A per-target decision no longer triggers such a read, so nothing
   * here resets a decision the operator has just made.
   */
  useEffect(() => {
    setTargetDecisions(seedTargetDecisions(eWarrant?.services ?? []));
  }, [eWarrant?.services]);

  /**
   * Re-reads the e-warrant so the view reflects what Xcipio now holds — used both after
   * a decision and after a successful generate. Shows the blocking loading popup for as
   * long as the read is in flight.
   *
   * Deliberately never rejects. It is awaited after a decision or a generation has
   * already gone through, and a refresh that fails does not undo either — reporting it
   * as a failed approval, rejection or generation would be wrong, so it reports itself.
   */
  const refreshDetail = async () => {
    if (selectedEWarrantId == null || selectedSenderId == null || selectedSenderId === '') {
      return;
    }

    setIsRefreshingDetail(true);

    try {
      // redux-promise-middleware makes the dispatched action awaitable — see below.
      await (getEWarrantDetail(selectedEWarrantId, selectedSenderId) as unknown as Promise<unknown>);
    } catch (error) {
      showError(t('eWarrant.view.refreshFailed', {
        error: describeError(error as AxiosError<ServerResponse<void>> | Error),
      }));
    } finally {
      setIsRefreshingDetail(false);
    }
  };

  const handleClickClose = () => goToEWarrantList(history);

  const renderFormPanel = () => (
    <FormPanel title={t('eWarrant.view.title')}>
      <FormButtons isEditing={false} onClickClose={handleClickClose} />
    </FormPanel>
  );

  if (eWarrant == null) {
    return (
      <div data-testid="EWarrantView">
        <FormLayout>
          <FormLayoutContent>
            {renderFormPanel()}
            <Callout intent="warning" className="e-warrant-view-callout">
              {t('eWarrant.view.notFound')}
            </Callout>
          </FormLayoutContent>
        </FormLayout>
      </div>
    );
  }

  const { authorisation, services, documents } = eWarrant;
  const isPending = eWarrant.status === EWarrantStatus.Pending;
  // An update to an authorisation whose warrant Xcipio has already provisioned. Approving
  // it has to sync that warrant's dates instead of the normal first-approval flow, which
  // leaves the newly-provisioned warrant to a separate Generate Warrant click — see
  // submitDecision.
  const isUpdateApproval = eWarrant.rawStatus === AWAITING_UPDATE_APPROVAL_STATUS;
  // adaptAuthStatus maps AwaitingApproval and AwaitingUpdateApproval alike to Pending, so
  // the Authorisation tab needs its own label to tell an update approval apart from a
  // first-time one — see EWarrantStatusCellRenderer's label override.
  const authorisationStatusLabel = isPending && isUpdateApproval
    ? t('eWarrant.view.authorisation.pendingUpdate')
    : undefined;
  const hasPrivilegeToDecide = privileges.includes(EWarrantPrivileges.Edit);
  // The two decision columns are an input only while the e-warrant itself is undecided;
  // afterwards they read out the DOCSTATUS Xcipio holds.
  const areDocumentsDecidable = isPending && hasPrivilegeToDecide && decisionInFlight == null;
  // Approving an e-warrant is approving what it carries, so the Approve button stays closed
  // until every document is ticked Approved and every target it tasks has been approved on
  // its own tab. Rejecting is not gated by either: a rejection stands on its own, whatever
  // was decided about the documents and the targets.
  const areDocumentsApproved = areAllDocumentsApproved(documents, documentDecisions);
  const areTargetsApproved = areAllTargetsApproved(services, targetDecisions);
  const canApprove = areDocumentsApproved && areTargetsApproved;
  // A target is decidable for exactly as long as the e-warrant itself is: the decision is
  // recorded against Xcipio's EWSLITASK row, and once the e-warrant is decided the tabs read
  // out what Xcipio holds rather than offering to change it.
  // A decision on the e-warrant itself closes the target buttons while it is in flight: it
  // is about to move every task's status, so a target decided behind it would be overwritten
  // by an approval the operator had already committed to.
  const areTargetsDecidable = isPending && hasPrivilegeToDecide && decisionInFlight == null;

  /**
   * The targets standing in the way of Generate Warrant, kept with the index the service
   * tabs number them by so the tooltip can name them the way the Services tab does.
   *
   * Both of the task's ETSI statuses are checked, and either one is enough on its own:
   * `actualStatus` is what the view labels Actual Status and `currentStatus` what it
   * labels Current Status — see renderServicePanel. The status carried through to the
   * tooltip is whichever of the two blocks, Actual Status winning when both do.
   *
   * `taskStatus` is not among them: it holds the operator's own decision, not a reason the
   * LEA gave for the task not to be provisioned.
   */
  const blockedTargets = services
    .map((service, index) => ({
      service,
      index,
      blockingStatus: [service.actualStatus, service.currentStatus].find(blocksGenerate),
    }))
    .filter(({ blockingStatus }) => blockingStatus != null);

  /**
   * The targets standing in the way of approving the e-warrant — rejected, or still
   * undecided — kept with the index the service tabs number them by so the warning on the
   * Services tab can name them the way that tab does.
   *
   * Distinct from blockedTargets above, which is about generating a warrant for an e-warrant
   * that was already approved: these two gate different buttons on different statuses.
   */
  const targetsBlockingApproval = getBlockingTargets(services, targetDecisions);

  const handleDecideDocument = (docId: string, status: EWarrantDecisionStatus) =>
    setDocumentDecisions((current) => toggleDocumentDecision(current, docId, status));

  const showGenerateError = (error: string) =>
    showError(t('eWarrant.generate.failed', { name: authorisation.name, error }));

  /** Throws when the server answered 200 but reported a failure in the body. */
  const rejectOnServerError = (response: any) => {
    const serverResponse: ServerResponse<unknown> = response?.value?.data;

    if (serverResponse?.error) {
      throw new Error(serverResponse.error.message);
    }

    return response;
  };

  /**
   * The message to show for a failure, which arrives either as an axios rejection or as
   * an Error thrown out of rejectOnServerError above.
   */
  const describeError = (error: AxiosError<ServerResponse<void>> | Error): string => (
    error instanceof Error && !(error as AxiosError).isAxiosError
      ? error.message
      : extractErrorMessage(error as AxiosError<ServerResponse<void>>)
  );

  const handleDownload = async (document: EWarrantDocument) => {
    try {
      await downloadDocument(authorisation.SENDERID, document);
    } catch (error) {
      showError(t('eWarrant.view.document.downloadFailed', {
        name: document.documentName,
        error: describeError(error as AxiosError<ServerResponse<void>> | Error),
      }));
    }
  };

  /**
   * Records the operator's decision. The e-warrant's AUTHSTATUS is moved in Xcipio to
   * the decision, and the message that explains it travels with it — the backend writes
   * that message to the ETSI approval description of the authorisation, and to ERRMSG on
   * the same EWSAUTH row.
   *
   * The note the operator typed is stamped with when the decision was made and who made
   * it before it is sent: the authorisation has nowhere else to record either, so all
   * three travel as the one message and are split back out for display.
   *
   * The decisions the operator ticked against the individual documents travel with it. The
   * backend writes each one's DOCSTATUS before it moves AUTHSTATUS, so a document that
   * could not be written leaves the e-warrant pending and the whole decision retryable —
   * which is why nothing here treats the document writes as a separate outcome.
   *
   * Xcipio owns the decision state, so the e-warrant is re-read rather than patched in
   * the store: what the bar then shows is what Xcipio actually holds. A failed write
   * leaves the bar on Pending with the note still typed instead of showing a decision
   * that was never recorded.
   *
   * Approving an AwaitingUpdateApproval e-warrant also sends the authorisation's current
   * start/end time, which tells the backend to find the warrant already provisioned for
   * this e-warrant and revise its dates — that e-warrant's warrant already exists, so this
   * is a different flow from the normal first approval, which leaves warrant creation to a
   * separate Generate Warrant click.
   */
  const submitDecision = async (status: EWarrantDecisionStatus) => {
    const isApproval = status === EWarrantStatus.Approved;

    setDecisionInFlight(status);

    try {
      // redux-promise-middleware resolves with { value: AxiosResponse } — see WarrantRetryPopup.
      const response = await (updateAuthStatus(
        authorisation.HI1OBJECTID,
        authorisation.SENDERID,
        status,
        buildDecisionMessage(userName, decisionNote),
        buildDocumentStatusPayload(documents, documentDecisions),
        isApproval && isUpdateApproval ? authorisation.startTime : undefined,
        isApproval && isUpdateApproval ? authorisation.endTime : undefined,
      ) as unknown as Promise<any>);

      rejectOnServerError(response);

      // Xcipio owns the decision, so the view is re-read rather than patched: this is the
      // same read the view mounts with, and it brings back the new AUTHSTATUS and the note
      // that was just recorded against the row. Awaited so the decision bar flips on the
      // refreshed e-warrant rather than on the pre-decision one.
      await refreshDetail();

      // Only the note is cleared. The document decisions are deliberately left alone: the
      // refresh above has just reseeded them from what Xcipio now holds, which is exactly
      // what the columns - read-only from here on - have to show.
      setDecisionNote('');
      showSuccessMessage(
        t(isApproval ? 'eWarrant.approve.success' : 'eWarrant.reject.success', { name: authorisation.name }),
        TOAST_TIMEOUT,
      );
    } catch (error) {
      showError(t(isApproval ? 'eWarrant.approve.failed' : 'eWarrant.reject.failed', {
        name: authorisation.name,
        error: describeError(error as AxiosError<ServerResponse<void>> | Error),
      }));
    } finally {
      setDecisionInFlight(null);
    }
  };

  /**
   * Records the operator's decision on one target. The backend finds that target's EWSLITASK
   * row by its LIID and moves both TASKSTATUS and the task:Status its ETSI payload carries;
   * task:DesiredStatus — what the LEA originally asked for — is never touched.
   *
   * Xcipio owns the status, so the e-warrant is re-read rather than patched in the store,
   * exactly as submitDecision does: what the tab then shows is what Xcipio actually holds,
   * and a failed write leaves the target undecided instead of showing a decision that was
   * never recorded.
   *
   * Unlike the e-warrant's own decision this is not a one-way door — while the e-warrant is
   * still Pending the opposite button stays live, so a target decided by mistake can be
   * decided again.
   */
  const submitTargetDecision = async (
    service: EWarrantServices,
    index: number,
    status: EWarrantTaskStatus,
  ) => {
    const isApproval = status === EWarrantTaskStatus.Active;
    const target = getServiceTabTitle(service, index);

    setTargetInFlight({ reference: service.reference, status });

    try {
      // redux-promise-middleware resolves with { value: AxiosResponse } — see submitDecision.
      const response = await (updateTaskStatus(
        authorisation.HI1OBJECTID,
        authorisation.SENDERID,
        service.reference,
        status,
      ) as unknown as Promise<any>);

      // Everything that could have gone wrong has by now: axios rejects a non-2xx, and this
      // turns a 200 carrying an error body into a throw. Reaching here is the write having
      // been accepted, so the decision is recorded from what was sent rather than read back.
      rejectOnServerError(response);

      // Only this target moves, and only in local state. Re-reading the whole e-warrant
      // would raise the blocking progress popup once per target, which on a multi-target
      // e-warrant is most of the work the operator came here to do.
      setTargetDecisions((current) => recordTargetDecision(current, service.reference, status));

      showSuccessMessage(
        t(isApproval ? 'eWarrant.target.approve.success' : 'eWarrant.target.reject.success', { target }),
        TOAST_TIMEOUT,
      );
    } catch (error) {
      showError(t(isApproval ? 'eWarrant.target.approve.failed' : 'eWarrant.target.reject.failed', {
        target,
        error: describeError(error as AxiosError<ServerResponse<void>> | Error),
      }));
    } finally {
      setTargetInFlight(null);
    }
  };

  const handleApprove = () => submitDecision(EWarrantStatus.Approved);

  const handleReject = () => submitDecision(EWarrantStatus.Rejected);

  /**
   * Generate warrant flow:
   *   1. Derive what this delivery function has enabled from the ontology
   *   2. Resolve the collection function template each service's handover format maps
   *      to, build a collection function with every field at its template default, and
   *      keep only the ones whose CF type is actually enabled here
   *   3. Pick out the case options templates the generated cases point at
   *   4. Resolve the LEA whose SENDERID matches the e-warrant's authorisation
   *   5. POST the e-warrant, those collection functions, the LEA, the ontology and those
   *      templates to have the warrant generated
   *
   * The intercept status is not moved from here: the backend owns it, and setting it to
   * Inprogress before generating left it there for good whenever generation then failed.
   */
  const handleGenerateWarrant = () => {
    if (supportedFeatures == null) {
      showGenerateError(t('eWarrant.generate.noSupportedFeatures'));
      return;
    }

    // Same derivation the warrant wizard relies on: which AF types, CF types and
    // services the ontology's feature nodes actually enable for this delivery
    // function. Construction is synchronous and makes no network calls.
    const warrantConfiguration = new WarrantConfiguration({
      warrants: { config: supportedFeatures },
      global: { selectedDF },
      discoveryXcipio: { discoveryXcipioSettings: { deployment } },
      // Unused here: WarrantConfiguration only touches these in its async methods,
      // and the generate flow calls none of them. Wired anyway so this instance
      // behaves like the wizard's if that ever changes.
      getAllAFsByFilter: getAllAFsByFilter as unknown as LookupByFilter,
      fetchCollectionFunctionListByFilter: fetchCollectionFunctionListByFilter as unknown as LookupByFilter,
    });

    // The deployment's default warrant template, or its first if none is flagged as the
    // default — the same preference the template pickers apply.
    const warrantTemplate = warrantTemplates.find(({ categoryDefault }) => categoryDefault)
      ?? warrantTemplates[0];

    const enabledCfTypeList = warrantConfiguration.getEnabledCfTypeList();

    // A template can exist for a handover format that this delivery function cannot
    // actually collect on. Creating that CF would produce a warrant that never
    // delivers, so drop it here and fail if nothing survives.
    const collectionFunctions = buildCollectionFunctionsFromEWarrant(
      eWarrant,
      collectionFunctionTemplateContext,
    ).filter(({ tag }) => enabledCfTypeList.includes(tag as string));

    if (collectionFunctions.length === 0) {
      showGenerateError(t('eWarrant.generate.noCollectionFunctionTemplate'));
      return;
    }

    // Each case options template travels with its values resolved the same way the
    // warrant form resolves them, so a generated case carries the same options a
    // form-created one would rather than a block of nulls. The warrant template is
    // identity only — the backend just stamps its id as the warrant's templateUsed.
    const templates: EWarrantGenerateTemplate[] = [
      ...caseOptionsTemplates
        .filter(({ name }) => CASE_OPTIONS_TEMPLATE_NAMES.includes(name))
        .map(({ id, name, category }) => ({
          id,
          name,
          category,
          options: getCaseOptionTemplateValue(caseOptionsTemplates, id),
        })),
      ...(warrantTemplate == null ? [] : [{
        id: warrantTemplate.id,
        name: warrantTemplate.name,
        category: warrantTemplate.category,
      }]),
    ];

    const lea = findLeaForEWarrant(eWarrant, collectionFunctionTemplateContext.collectionFunctionGroupList);

    if (lea == null) {
      showGenerateError(t('eWarrant.generate.noLea', { senderId: authorisation.SENDERID }));
      return;
    }

    setIsGenerating(true);

    // redux-promise-middleware resolves with { value: AxiosResponse } — see WarrantRetryPopup.
    const promise: any = generateWarrant(eWarrant, collectionFunctions, lea, {
      supportedFeatures,
      enabledAfTypeList: warrantConfiguration.getEnabledAfTypeList(),
      enabledServices: warrantConfiguration.getEnabledServices(),
      templates,
      // The same list that fills the collection function form's ownnetid dropdown —
      // the backend picks the default network for each created CF's destination.
      networks: collectionFunctionTemplateContext.networks,
    });

    promise
      .then(rejectOnServerError)
      .then(async (response: { value: AxiosResponse<ServerResponse<EWarrantGeneration>> }) => {
        // The backend resolves the actual name Xcipio stores for the generated warrant
        // (a truncated AUTHREFERENCE plus a hash suffix) — it differs from the e-warrant's
        // own name/reference, so the toast names the warrant using this rather than
        // guessing from e-warrant data that was never going to match.
        const generatedWarrantName = response?.value?.data?.data?.[0]?.warrantName ?? authorisation.name;

        // The detail read picks up the case/services Xcipio now has provisioned, so the
        // view is reloaded before the success toast is shown.
        await refreshDetail();

        showSuccessMessage(t('eWarrant.generate.success', { name: generatedWarrantName }), TOAST_TIMEOUT);
      })
      .catch((error: AxiosError<ServerResponse<void>> | Error) => showGenerateError(describeError(error)))
      .finally(() => setIsGenerating(false));
  };

  const renderDecisionBar = () => {
    if (!isPending) {
      const isApproved = eWarrant.status === EWarrantStatus.Approved;

      // When and by whom are read out of the decision message itself — the approval
      // description is the only place the authorisation records them.
      const decision = parseDecisionMessage(eWarrant.decisionNote);
      const decided = {
        status: eWarrant.status,
        decidedAt: formatDateTime(decision.decidedAt ?? eWarrant.decidedAt),
        decidedBy: decision.decidedBy ?? eWarrant.decidedBy ?? NOT_AVAILABLE,
      };

      return (
        <div className={decisionBarClass(isApproved ? 'approved' : 'rejected')}>
          <Callout
            className="e-warrant-view-decision-callout"
            intent={isApproved ? 'success' : 'danger'}
          >
            <Trans
              i18nKey={decision.text ? 'eWarrant.view.decisionWithMessage' : 'eWarrant.view.decision'}
              values={{ ...decided, message: decision.text }}
              components={{ bold: <strong /> }}
            />
          </Callout>
          {isApproved
            && hasPrivilegeToDecide
            && eWarrant.interceptStatus !== EWarrantInterceptStatusValue.CreateSuccess && (
            /*
             * Why Generate Warrant is closed, said out loud: the tooltip names every
             * target that blocks it and the status it is in, because the operator has to
             * go to the Services tab to see either of those.
             */
            <Tooltip2
              content={t('eWarrant.generate.targetsNotActive', {
                targets: blockedTargets
                  .map(({ service, index, blockingStatus }) => t('eWarrant.generate.blockedTarget', {
                    target: getServiceTabTitle(service, index),
                    status: blockingStatus,
                  }))
                  .join(', '),
              })}
              disabled={blockedTargets.length === 0}
            >
              <Button
                data-testid="generate-warrant-button"
                icon="document-share"
                text={t('eWarrant.grid.generateWarrant')}
                loading={isGenerating}
                disabled={blockedTargets.length > 0}
                onClick={handleGenerateWarrant}
              />
            </Tooltip2>
          )}
        </div>
      );
    }

    if (!hasPrivilegeToDecide) {
      return null;
    }

    return (
      <div className={decisionBarClass('pending')}>
        {/*
          * Why Approve is closed, said out loud. This bar sits above the tab card, so an
          * operator looking at the Authorisation tab would otherwise see a disabled button
          * with nothing on screen to explain it.
          */}
        <InputGroup
          className="e-warrant-view-decision-note"
          placeholder={t('eWarrant.view.decisionNotePlaceholder')}
          value={decisionNote}
          disabled={decisionInFlight != null}
          onChange={({ target }) => setDecisionNote(target.value)}
        />
        <Tooltip2
          content={renderApprovalBlockers(approvalBlockedReasons())}
          disabled={canApprove}
        >
          <Button
            data-testid="approve-button"
            intent="success"
            icon="tick"
            text={t('eWarrant.grid.approve')}
            loading={decisionInFlight === EWarrantStatus.Approved}
            disabled={decisionInFlight === EWarrantStatus.Rejected || !canApprove}
            onClick={handleApprove}
          />
        </Tooltip2>
        <Button
          data-testid="reject-button"
          intent="danger"
          icon="cross"
          text={t('eWarrant.view.reject')}
          loading={decisionInFlight === EWarrantStatus.Rejected}
          disabled={decisionInFlight === EWarrantStatus.Approved}
          onClick={handleReject}
        />
      </div>
    );
  };

  /**
   * The authorisation fields, in lines rather than in one flowing grid: each group below
   * is its own grid, so the fields that belong together stay on the same line and are not
   * pushed apart by whatever precedes them. Every grid computes the same column tracks
   * from the panel width, so the lines still align with each other.
   */
  const renderAuthorisationPanel = () => (
    <div className="e-warrant-view-panel">
      <div className="e-warrant-view-field-rows">
        <EWarrantKeyValueGrid
          items={[
            { label: t('eWarrant.view.authorisation.reference'), value: authorisation.reference, isMonospaced: true },
            { label: t('eWarrant.view.authorisation.priority'), value: authorisation.priority },
            {
              label: t('eWarrant.view.authorisation.senderId'),
              value: authorisation.SENDERID,
              isMonospaced: true
            },
          ]}
        />
        <EWarrantKeyValueGrid
          items={[
            // AUTHSTATUS as the portal reads it, alongside how far the CSP side
            // provisioning of the warrant it authorises has got.
            {
              label: t('eWarrant.view.authorisation.currentStatus'),
              renderValue: () => (
                <EWarrantStatusCellRenderer value={eWarrant.status} label={authorisationStatusLabel} />
              ),
            },
            {
              label: t('eWarrant.grid.interceptStatus'),
              renderValue: () => <EWarrantStatusCellRenderer value={eWarrant.interceptStatus} />,
            },
          ]}
        />
        <EWarrantKeyValueGrid
          items={[
            {
              label: t('eWarrant.view.authorisation.creationTimestamp'),
              value: formatDateTime(authorisation.creationTimestamp)
            },
            { label: t('eWarrant.view.authorisation.startTime'), value: formatDateTime(authorisation.startTime) },
            { label: t('eWarrant.view.authorisation.endTime'), value: formatDateTime(authorisation.endTime) },
          ]}
        />
        <EWarrantKeyValueGrid
          items={[
            { label: t('eWarrant.view.authorisation.typeOfCase'), value: authorisation.typeOfCase },
            { label: t('eWarrant.view.authorisation.countryCode'), value: authorisation.countryCode },
            { label: t('eWarrant.view.authorisation.cspId'), value: authorisation.cspId, isMonospaced: true },
            { label: t('eWarrant.view.authorisation.flags'), value: authorisation.flags.join(', ') },
          ]}
        />
      </div>
      <h4 className="e-warrant-view-subhead">{t('eWarrant.view.approval.subhead')}</h4>
      <EWarrantKeyValueGrid
        items={[
          { label: t('eWarrant.view.authorisation.jurisdiction'), value: authorisation.jurisdiction },
          { label: t('eWarrant.view.authorisation.legalEntity'), value: authorisation.legalEntity },
          // The ETSI auth:AuthorisationLegalType, read down to its DictionaryEntry value.
          { label: t('eWarrant.view.authorisation.legalType'), value: authorisation.legalType },
          { label: t('eWarrant.view.approval.approverName'), value: authorisation.approval.approverName },
          { label: t('eWarrant.view.approval.approverRole'), value: authorisation.approval.approverRole },
          { label: t('eWarrant.view.approval.approverEmail'), value: authorisation.approval.approverEmail },
          { label: t('eWarrant.view.approval.approverPhone'), value: authorisation.approval.approverPhone },
          { label: t('eWarrant.view.approval.timestamp'), value: formatDateTime(authorisation.approval.timestamp) },
          {
            label: t('eWarrant.view.approval.isEmergency'),
            value: authorisation.approval.isEmergency ? t('common.grid.yes') : t('common.grid.no')
          },
        ]}
      />
      {/*
        * auth:AuthorisationManualInformation - free text the LEA sent with the
        * authorisation, which fits none of the fields above. The section is shown even
        * when the authorisation carries none, so its absence reads as "nothing was sent"
        * rather than as a section that failed to render.
        */}
      <h4 className="e-warrant-view-subhead">{t('eWarrant.view.manualInformation')}</h4>
      {authorisation.manualInformation
        ? <p className="e-warrant-view-manual-information">{authorisation.manualInformation}</p>
        : <span className="e-warrant-view-muted">{NOT_AVAILABLE}</span>}
    </div>
  );

  const renderDocumentsPanel = () => (
    <div className="e-warrant-view-panel">
      <EWarrantDocumentTable
        documents={documents}
        decisions={documentDecisions}
        isDecidable={areDocumentsDecidable}
        onDownload={handleDownload}
        onDecide={handleDecideDocument}
      />
    </div>
  );

  /**
   * The decision on one target, at the foot of its own tab: the two buttons while the
   * e-warrant is still Pending, and the status Xcipio holds once it is not.
   *
   * The button matching the target's current decision is closed and the opposite one stays
   * open, so a decision can be corrected but not pointlessly re-applied. A target that
   * arrived without a LIID cannot be addressed in the payload at all, and one Xcipio has
   * since expired is past deciding either way, so both get the read-only treatment rather
   * than buttons that could not be honoured.
   */
  const renderTargetActions = (service: EWarrantServices, index: number) => {
    const decision = getTargetDecision(service, targetDecisions);
    // Any decision in flight closes every target's buttons, so two writes cannot race each
    // other into the decisions map.
    const isBusy = targetInFlight != null;
    const isThisTarget = targetInFlight?.reference === service.reference;

    if (!areTargetsDecidable || !isTargetDecidable(service)) {
      return (
        <div className={targetActionsClass(decision)}>
          <span className="e-warrant-view-target-actions-label">
            {t('eWarrant.view.services.decision')}
          </span>
          <EWarrantStatusCellRenderer value={decision ?? (service.taskStatus || NOT_AVAILABLE)} />
        </div>
      );
    }

    return (
      <div className={targetActionsClass(decision)}>
        <span className="e-warrant-view-target-actions-label">
          {t('eWarrant.view.services.decideTarget', { target: getServiceTabTitle(service, index) })}
        </span>
        <Button
          data-testid={`target-approve-button-${index}`}
          intent="success"
          icon="tick"
          text={t('eWarrant.grid.approve')}
          loading={isThisTarget && targetInFlight?.status === EWarrantTaskStatus.Active}
          disabled={isBusy || decision === EWarrantTaskStatus.Active}
          onClick={() => submitTargetDecision(service, index, EWarrantTaskStatus.Active)}
        />
        <Button
          data-testid={`target-reject-button-${index}`}
          intent="danger"
          icon="cross"
          text={t('eWarrant.view.reject')}
          loading={isThisTarget && targetInFlight?.status === EWarrantTaskStatus.Rejected}
          disabled={isBusy || decision === EWarrantTaskStatus.Rejected}
          onClick={() => submitTargetDecision(service, index, EWarrantTaskStatus.Rejected)}
        />
      </div>
    );
  };

  const renderServicePanel = (service: EWarrantServices, index: number) => (
    <div className="e-warrant-view-panel">
      <EWarrantKeyValueGrid
        items={[
          { label: t('eWarrant.view.services.reference'), value: service.reference, isMonospaced: true },
          // A decision taken since the last read wins: it is what Xcipio now holds, and the
          // e-warrant is no longer re-read after one, so `actualStatus` still carries the
          // ETSI task:Status the task had when the view loaded. currentStatus below is the
          // LEA's own task:DesiredStatus, which no decision writes, so it is shown as it came.
          {
            label: t('eWarrant.view.services.actualStatus'),
            value: getTargetDecision(service, targetDecisions) ?? service.actualStatus
          },
          { label: t('eWarrant.view.services.currentStatus'), value: service.currentStatus },
          { label: t('eWarrant.view.services.deliveryType'), value: service.deliveryType },
          { label: t('eWarrant.view.services.handoverFormat'), value: service.handoverFormat },
          {
            label: t('eWarrant.view.services.deliveryEndpoint'),
            value: service.deliveryEndpoint,
            isMonospaced: true
          },
          {
            label: t('eWarrant.view.services.ccDeliveryEndpoint'),
            value: service.ccDeliveryEndpoint,
            isMonospaced: true
          },
          { label: t('eWarrant.view.services.cspId'), value: service.cspId, isMonospaced: true },
          {
            label: t('eWarrant.view.services.provisioningTime'),
            value: formatDateTime(service.provisioningTime)
          },
          {
            label: t('eWarrant.view.services.deprovisioningTime'),
            value: formatDateTime(service.deprovisioningTime)
          },
          { label: t('eWarrant.view.services.startTime'), value: formatDateTime(service.startTime) },
          { label: t('eWarrant.view.services.endTime'), value: formatDateTime(service.endTime) },
        ]}
      />
      <h4 className="e-warrant-view-subhead">{t('eWarrant.view.services.serviceTypes')}</h4>
      <div className="e-warrant-view-chips">
        {service.serviceTypes.length === 0
          ? <span className="e-warrant-view-muted">{NOT_AVAILABLE}</span>
          : service.serviceTypes.map((serviceType) => (
            <span className="e-warrant-view-chip" key={serviceType}>{serviceType}</span>
          ))}
      </div>
      <h4 className="e-warrant-view-subhead">{t('eWarrant.view.services.targetIdentifiers')}</h4>
      {service.targetIdentifiers.length === 0 ? (
        <span className="e-warrant-view-muted">{NOT_AVAILABLE}</span>
      ) : (
        <EWarrantKeyValueGrid
          items={service.targetIdentifiers.map(({ format, value }) => ({
            label: format,
            value,
            isMonospaced: true
          }))}
        />
      )}
      {renderTargetActions(service, index)}
    </div>
  );

  /**
   * Service tabs are labelled by the target they task, so an e-warrant covering
   * several targets under one authorisation stays readable.
   */
  const getServiceTabTitle = (service: EWarrantServices, index: number) =>
    t('eWarrant.view.services.targetTab', {
      index: index + 1,
      target: service.targetIdentifiers?.[0]?.value || service.reference || NOT_AVAILABLE,
    });

  /**
   * The targets still standing in the way of approving the e-warrant: the line introducing
   * them, then one entry per target, named the way the service tabs name it and carrying the
   * state it is in.
   *
   * Shared by the Services tab warning and by the Approve button's own tooltip, so the two
   * always say the same thing.
   */
  const targetsBlockingApprovalReasons = (): ApprovalBlocker[] => [
    { text: t('eWarrant.target.notAllApproved') },
    ...targetsBlockingApproval.map(({ service, index, decision }) => ({
      text: t('eWarrant.target.blocked', {
        target: getServiceTabTitle(service, index),
        status: decision ?? t('eWarrant.target.awaitingDecision'),
      }),
      isDetail: true,
    })),
  ];

  /**
   * Why Approve is closed, said out loud — for the button's tooltip and for the hint beside
   * it. Both halves are reported when both block, since fixing only the one that was named
   * would leave the button just as closed.
   *
   * Empty when nothing blocks, which is also when the tooltip is disabled.
   */
  const approvalBlockedReasons = (): ApprovalBlocker[] => [
    ...(areDocumentsApproved ? [] : [{ text: t('eWarrant.view.document.notAllApproved') }]),
    ...(areTargetsApproved ? [] : targetsBlockingApprovalReasons()),
  ];

  /**
   * The Services tab label, carrying a warning whenever a target still has to be decided
   * before the e-warrant can be approved. The decision lives a tab away, so without this the
   * operator would meet a closed Approve button with nothing on screen explaining it.
   */
  const renderServicesTabTitle = () => {
    const title = `${t('eWarrant.view.tab.services')} (${services.length})`;

    if (!isPending || !hasPrivilegeToDecide || targetsBlockingApproval.length === 0) {
      return title;
    }

    return (
      <span className="e-warrant-view-tab-title">
        {title}
        <Tooltip2 content={renderApprovalBlockers(targetsBlockingApprovalReasons())}>
          <Icon icon="warning-sign" intent="warning" data-testid="services-tab-warning" />
        </Tooltip2>
      </span>
    );
  };

  const renderServicesPanel = () => {
    if (services.length === 0) {
      return (
        <div className="e-warrant-view-panel">
          <span className="e-warrant-view-muted">{t('eWarrant.view.noServices')}</span>
        </div>
      );
    }

    return (
      <Tabs
        id="e-warrant-view-service-tabs"
        className="e-warrant-view-service-tabs"
        selectedTabId={selectedServiceTab}
        onChange={(tabId) => setSelectedServiceTab(tabId as string)}
      >
        {services.map((service, index) => (
          <Tab
            key={`${service.reference}-${index}`}
            id={`${SERVICE_TAB_ID_PREFIX}${index}`}
            title={getServiceTabTitle(service, index)}
            panel={renderServicePanel(service, index)}
          />
        ))}
      </Tabs>
    );
  };

  return (
    <div data-testid="EWarrantView" className="e-warrant-view">
      <RetrievingDataProgress isOpen={isRefreshingDetail} message={t('eWarrant.view.refreshing')} />
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel()}

          <div className="e-warrant-view-head">
            <div className="e-warrant-view-title">
              <div>
                <h3>{authorisation.name}</h3>
                <p>{authorisation.description || t('eWarrant.view.noDescription')}</p>
              </div>
            </div>
            <div className="e-warrant-view-meta">
              <span>
                <strong>{t('eWarrant.grid.sourceAgency')}:</strong> {authorisation.sourceAgency || NOT_AVAILABLE}
              </span>
              <span>
                <strong>{t('eWarrant.grid.receivingCsp')}:</strong> {authorisation.receivingCsp || NOT_AVAILABLE}
              </span>
              <span>
                <strong>{t('eWarrant.grid.receivedAt')}:</strong> {formatDateTime(authorisation.receivedAt)}
              </span>
              <span>
                <strong>{t('eWarrant.view.sourceReference')}:</strong> {authorisation.sourceWarrantId}
              </span>
            </div>
          </div>

          {renderDecisionBar()}

          <div className="e-warrant-view-card">
            <Tabs
              id="e-warrant-view-tabs"
              large
              selectedTabId={selectedTab}
              onChange={(tabId) => setSelectedTab(tabId as EWarrantViewTab)}
            >
              <Tab
                id={EWarrantViewTab.Authorisation}
                title={t('eWarrant.view.tab.authorisation')}
                panel={renderAuthorisationPanel()}
              />
              <Tab
                id={EWarrantViewTab.Documents}
                title={`${t('eWarrant.view.tab.documents')} (${documents.length})`}
                panel={renderDocumentsPanel()}
              />
              <Tab
                id={EWarrantViewTab.Services}
                title={renderServicesTabTitle()}
                panel={renderServicesPanel()}
              />
            </Tabs>
          </div>
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(EWarrantView));
